import React, { useState, useCallback, useEffect } from 'react'
import { Accordion, AccordionDetails, Pagination } from '@mui/material'
import { Box, Divider, Grid } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { CheckoutDocumentInput, CoBillFragment, CoDocumentDetailFragment, DocumentType } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { AccordionSummary } from './accordion-summary'
import { HicDataGridPro } from '../../../../../components/hic-vapor/datagrid/datagrid'
import {
  GridCellModesModel,
  GridColDef,
  GridEventListener,
  GridRowId,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  GridCellModes,
} from '@mui/x-data-grid-pro'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { keysOf } from 'src/utils/utils'
import { CoQuery } from './co-query-wrapper'
import { getBillCheckoutInput, reduceProductionAssigneds } from '../checkout-document-form.utils'
import { Currency } from 'src/api/currency'
import { HICTitleSmall } from '../../../../../components/typography/title-small'
import { useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { GridStartCellEditModeParams } from '@mui/x-data-grid/models/api/gridEditingApi'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { faSave, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { DocumentDetailsSchema } from '../checkout-document-form.schema'
import { Action } from 'src/app/components/data-grid-action-cell-item'

export type CoDetailsProps = {
  data: CoQuery
  onChange: (details: DocumentDetailsSchema) => void
  isTouched: (status: boolean) => void
}

export default function CoDetails({ data, onChange, isTouched }: CoDetailsProps) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()

  const [cellOnEdit, setCellOnEdit] = useState<string>()
  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({})
  const [rows, setRows] = React.useState<DetailRow[]>([])
  const [otherDetails, setOtherDetails] = React.useState<CoDocumentDetailWithProductionReference[]>([])

  const handleColumnsEdit = (rowId: GridRowId, field?: string, initialValue?: unknown) => {
    if (field) {
      const cell: GridStartCellEditModeParams = { id: rowId, field: field, initialValue: initialValue }
      apiRef.current.startCellEditMode({ ...cell })
    }
  }

  const handleColumnsCancelEdit = useCallback(
    (rowId: GridRowId) => {
      if (cellOnEdit) apiRef.current.stopCellEditMode({ id: rowId, field: cellOnEdit, ignoreModifications: true })
    },
    [cellModesModel, cellOnEdit],
  )

  const columns = useColumns(handleColumnsEdit, handleColumnsCancelEdit, cellModesModel)

  useEffect(() => {
    const { rows, otherDetails } = useRows(data)
    setRows(rows)
    setOtherDetails(otherDetails)
  }, [data])

  const apiRef = useGridApiRef()

  const handleCellEditStart: GridEventListener<'cellEditStart'> = (params) => {
    const { field } = params
    setCellOnEdit(field)
  }

  const processRowUpdate = useCallback(
    //
    async (newRow: DetailRow, oldRow: DetailRow) => {
      const updatedRows = [...rows.map((r) => (r.id === newRow.id ? newRow : r))]
      setRows(updatedRows)
      onChange(
        [...updatedRows.map<CoDocumentDetailWithProductionReference>(({ id, ...rest }) => rest), ...otherDetails].map((d) => ({
          amount: d.amount,
          date: d.date,
          description: d.description,
          quantity: d.quantity,
          reference: {
            accountingCategoryId: d.reference.accountantCategoryId ?? undefined,
            chargeCategoryId: d.reference.chargeCategoryId ?? undefined,
          },
          referenceDocumentId: d.referenceDocumentId ?? undefined,
          vatId: d.vat?.id ?? undefined,
          billId: d.billId ?? undefined,
          productionAssignedIs: d.productionAssignedId,
        })),
      )
      isTouched(true)
      return { ...newRow }
    },
    [rows, otherDetails],
  )

  const processRowUpdateError = useCallback(
    (error: unknown) => {
      showError(error)
    },
    [showError],
  )

  return (
    <>
      <Grid item xs={12} style={{ paddingTop: '0px' }}>
        <Accordion disableGutters elevation={0} square>
          <AccordionSummary style={{ paddingRight: '0px', paddingLeft: '0px' }}>
            <HICTitleSmall>{t('pms.common.checkout_document.details.title')}</HICTitleSmall>
          </AccordionSummary>
          <AccordionDetails style={{ paddingRight: '0px', paddingLeft: '0px' }}>
            <HicDataGridPro //
              sx={{
                '& .MuiDataGrid-cell--editable:hover': {
                  bgcolor: (theme) => theme.palette.action.hover,
                  borderBottomColor: (theme) => theme.palette.secondary.main,
                  color: (theme) => theme.palette.secondary.main,
                },
              }}
              {...hicCommonDataGridProProps}
              apiRef={apiRef}
              columns={columns}
              rows={rows}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 5 } } }}
              onCellEditStart={handleCellEditStart}
              cellModesModel={cellModesModel}
              onCellModesModelChange={(model) => setCellModesModel(model)}
              disableRowSelectionOnClick
              pageSizeOptions={[5]}
              pagination={rows.length > 5}
              hideFooter={rows.length <= 5}
              rowHeight={40}
              columnHeaderHeight={40}
              components={{
                Pagination: CustomPagination,
              }}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={processRowUpdateError}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12} style={{ paddingTop: '0px' }}>
        <Divider light />
      </Grid>
    </>
  )
}

type DetailRow = CoDocumentDetailWithProductionReference & { id: number }
type CoDocumentDetailWithProductionReference = CoDocumentDetailFragment & { productionAssignedId?: string }

function useRows(data: CoQuery): { rows: DetailRow[]; otherDetails: CoDocumentDetailWithProductionReference[] } {
  let documentDetails: CoDocumentDetailWithProductionReference[] = []

  if (data.mode === 'READ_ONLY' || data.mode === 'UPDATE') {
    documentDetails = data.document.documentDetails ?? []
  }
  if (data.mode === 'CREATE') {
    documentDetails = getDocumentDetailsFromProduction(data.bills)
  }

  const result = documentDetails //
    .filter((dd) => !dd.referenceDocument || dd.referenceDocument.documentType !== DocumentType.ADVANCE)
    .map((dd, i) => ({ id: i, ...dd }))

  const otherDetails = documentDetails.filter((dd) => dd.referenceDocument && dd.referenceDocument.documentType === DocumentType.ADVANCE)
  return { rows: result, otherDetails }
}

function useColumns(edit: (rowId: GridRowId, field?: string, initialValue?: unknown) => void, cancelEdit: (rowId: GridRowId) => void, cellModesModel: GridCellModesModel): GridColDef[] {
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()

  const f = keysOf<CoDocumentDetailFragment>()

  const editableSingleClickCell = (row: DetailRow, field: string, value: string, typedValue?: any, align: 'left' | 'center' | 'right' = 'left') => {
    return (
      <Box
        sx={{ alignItems: 'center', width: '100%', height: '100%', display: 'flex', justifyContent: align === 'left' ? 'start' : align === 'right' ? 'end' : 'center' }}
        onClick={() => {
          edit(row.id, field, typedValue ?? value)
        }}
      >
        {value}
      </Box>
    )
  }

  return [
    {
      field: f('date'),
      headerName: t('pms.common.checkout_document.details.date'),
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params) => formatDate(params.value),
      type: 'date',
    },

    {
      field: f('description'),
      headerName: t('pms.common.checkout_document.details.description'),
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      editable: true,
      renderCell: (params) => {
        const row = params.row as DetailRow
        return editableSingleClickCell(row, f('description'), row.description)
      },
    },
    {
      field: f('quantity'),
      headerName: t('pms.common.checkout_document.details.quantity'),
      width: 50,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: f('amount'),
      headerName: t('pms.common.checkout_document.details.amount'),
      align: 'right',
      width: 105,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params) => formatCurrency(params.value, currency),
    },
    {
      field: 'vatAmount',
      headerName: t('pms.common.checkout_document.details.vat'),
      width: 50,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (params) => {
        const row = params.row as CoDocumentDetailFragment
        return row.vat ? row.vat.code : '-'
      },
    },
    {
      field: f('totalAmount'),
      headerName: t('pms.common.checkout_document.details.total_amount'),
      width: 105,
      align: 'right',
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params) => formatCurrency(params.value, currency),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      minWidth: 52,
      width: 72,
      resizable: false,
      getActions: (params) => {
        const row = params.row as CoDocumentDetailFragment

        const editOnCurrentRow = cellModesModel[params.id] ? GridCellModes.Edit : GridCellModes.View
        if (editOnCurrentRow === GridCellModes.Edit)
          return [
            <Action icon={faSave} label={t('commons.edit')} />,
            <Action
              icon={faXmark}
              onClick={() => {
                cancelEdit(params.id)
              }}
              label={t('commons.cancel')}
            />,
          ]
        else return []
      },
    },
  ]
}

function CustomPagination() {
  const apiRef = useGridApiContext()
  const page = useGridSelector(apiRef, gridPageSelector)
  const pageCount = useGridSelector(apiRef, gridPageCountSelector)

  return <Pagination count={pageCount} page={page + 1} onChange={(event, value) => apiRef.current.setPage(value - 1)} />
}

function RoundCurrency(value: number, currency: Currency): number {
  // Value passed as integer, es: 100,10 => 10010
  if (currency === 'EUR') {
    return Math.round(value)
  } else {
    return +value.toFixed(0)
  }
}

export function getDocumentDetailsFromProduction(bills: CoBillFragment[], input?: CheckoutDocumentInput): CoDocumentDetailWithProductionReference[] {
  const pas = reduceProductionAssigneds(bills)
  const inputPas = getBillCheckoutInput(pas)
  const details = pas.reduce((dds: CoDocumentDetailWithProductionReference[], pa) => {
    const quotedAmount = RoundCurrency(pa.production.total * pa.quota, pa.production.currency)
    if (input?.fiscalType === 'CREDIT_NOTE' && quotedAmount > 0) {
      return dds
    }
    let amount = inputPas.flatMap((b) => b.productions).find((p) => p.productionAssignedId === pa.id)?.amount ?? quotedAmount
    if (Math.abs(amount) > Math.abs(quotedAmount)) {
      return dds
    }
    let quantity: number
    let total: number
    if (amount === quotedAmount && pa.quota === 1) {
      quantity = pa.production.quantity
      amount = pa.production.amount
      total = pa.production.total
    } else {
      quantity = 1
      total = amount
    }

    const dd: CoDocumentDetailWithProductionReference = {
      quantity: input?.fiscalType === 'CREDIT_NOTE' ? Math.abs(quantity) : quantity,
      amount: input?.fiscalType === 'CREDIT_NOTE' ? Math.abs(amount) : amount,
      date: pa.production.date,
      description: pa.production.description ?? pa.production.chargeCategory.name,
      totalAmount: input?.fiscalType === 'CREDIT_NOTE' ? Math.abs(total) : total,
      vat: {
        id: pa.production.vat.id,
        value: pa.production.vat.value,
        code: pa.production.vat.code,
      },
      reference: {
        accountantCategoryId: undefined,
        chargeCategoryId: undefined,
      },
      billId: pa.billId,
      productionAssignedId: pa.id,
    }
    return [...dds, dd]
  }, [])

  return details
}
